import React from 'react';
import Icon from '../../../components/AppIcon';

const OfferBandSection = ({ offerBand, confidence }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const calculatePercentage = (value, min, max) => {
    return ((value - min) / (max - min)) * 100;
  };

  const minPercentage = 0;
  const maxPercentage = 100;
  const rangeWidth = maxPercentage - minPercentage;

  return (
    <div className="w-full bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="DollarSign" size={24} color="var(--color-accent)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
            Recommended Offer Band
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
          <span className="text-sm text-muted-foreground text-caption">
            <span className="font-semibold text-data">{confidence}%</span>
          </span>
        </div>
      </div>
      <div className="space-y-6 md:space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="p-4 md:p-6 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingDown" size={20} color="var(--color-success)" />
              <span className="text-sm text-muted-foreground text-caption">Minimum Offer</span>
            </div>
            <div className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground text-data">
              {formatCurrency(offerBand?.min)}
            </div>
          </div>

          <div className="p-4 md:p-6 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={20} color="var(--color-warning)" />
              <span className="text-sm text-muted-foreground text-caption">Maximum Offer</span>
            </div>
            <div className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground text-data">
              {formatCurrency(offerBand?.max)}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span className="text-caption">Offer Range Visualization</span>
            <span className="text-caption">
              Spread: <span className="font-semibold text-data">{formatCurrency(offerBand?.max - offerBand?.min)}</span>
            </span>
          </div>

          <div className="relative w-full h-12 md:h-16 bg-muted rounded-lg overflow-hidden">
            <div 
              className="absolute top-0 bottom-0 bg-gradient-to-r from-success via-accent to-warning rounded-lg"
              style={{
                left: `${minPercentage}%`,
                width: `${rangeWidth}%`
              }}
            />
            
            <div className="absolute inset-0 flex items-center justify-between px-4">
              <div className="flex flex-col items-start">
                <span className="text-xs md:text-sm font-semibold text-white drop-shadow-lg">
                  {formatCurrency(offerBand?.min)}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs md:text-sm font-semibold text-white drop-shadow-lg">
                  {formatCurrency(offerBand?.max)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                Negotiation Strategy
              </h3>
              <p className="text-sm text-muted-foreground font-body leading-relaxed">
                Start your offer at the lower end of this band and be prepared to negotiate up to the maximum. Consider market conditions, days on market, and vendor motivation when positioning your initial offer.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferBandSection;